import { useEffect, useState } from 'react'
import { Button } from 'semantic-ui-react'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { DateTimePicker, TextField } from '@carsys/ui'
import moment from 'moment'
import { Dialog, DialogContent, DialogTitle, IconButton, TextareaAutosize } from '@mui/material'
import { TooltipButton } from '@cbgms/components/form/TooltipButton'
import { AlertMessage, AlertStatus, comparisonOptions, editableFields } from '../state/types'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { AddEditAlertMessage } from '../state/actions'
import CloseIcon from '@mui/icons-material/Close'
interface AlertProps {
  open: boolean
  togglePopup: () => void
  status: string
  appId: string
  callBackFetch: (AppId: string, status: string) => Promise<void>
  initialAlertData?: AlertMessage | null
  alertDataProp?: AlertMessage
  isEditMode?: boolean
}

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: '1px solid #e0e0e0'
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing ? theme.spacing(1) : 8,
    right: theme.spacing ? theme.spacing(1) : 8
  },
  closeButtonStyle: {
    backgroundColor: '#f0f0f0'
  },
  datePickerContainer: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    width: '100%'
  },
  datePicker: {
    padding: '8px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#333',
    outline: 'none',
    '&:focus': {
      borderColor: '#3b82f6'
    }
  },
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: 'white',
    width: '100%',
    height: '100%'
  },
  mainForm: {
    maxHeight: '800px',
    overflowY: 'auto',
    width: '100%'
  },
  formWrapper: {
    borderRadius: '4px'
  },
  editEntryHeader: {
    padding: '10px',
    fontWeight: 'bold',
    width: '100%',
    height: '50px',
    borderBottom: '1px solid #e0e0e0'
  },
  formContent: {
    padding: '16px',
    width: '100%'
  },
  formGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    width: '100%'
  },
  label: {
    width: '120px',
    minWidth: '120px'
  },
  textarea: {
    border: '1px solid #e5e7eb',
    padding: '8px',
    width: '100%',
    height: '96px'
  },
  radioGroup: {
    display: 'flex',
    gap: '16px',
    width: '100%'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center'
  },
  radio: {
    marginRight: '8px'
  },
  criteriaSection: {
    marginTop: '32px'
  },
  criteriaHeader: {
    fontWeight: '600',
    marginBottom: '16px',
    textAlign: 'center',
    width: '100%'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px'
  },
  backButton: {
    alignItems: 'flex-start'
  },
  criteriaContainer: {
    width: '100%',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: '210px',
    boxShadow: '0 0px 0px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12), 0 3px 1px -2px rgba(0,0,0,0.2)'
  },
  criteriaTableHeader: {
    display: 'flex',
    width: '100%',
    borderBottom: '1px solid #e0e0e0',
    padding: '4px'
  },
  headerColumn: {
    padding: '8px 16px',
    fontWeight: '500',
    color: '#333',
    fontSize: '14px',
    flex: 1
  },
  criteriaRow: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '8px 16px'
  },
  select: {
    width: '180px',
    height: '32px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '2px',
    fontSize: '14px',
    color: '#333',
    padding: '4px 8px',
    margin: '4px 8px',
    outline: 'none'
  },
  comparisonSelect: {
    width: '120px',
    height: '32px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '2px',
    fontSize: '14px',
    color: '#333',
    padding: '4px 8px',
    margin: '4px 8px',
    outline: 'none'
  },
  input: {
    flexGrow: 1,
    height: '32px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '2px',
    fontSize: '14px',
    color: '#333',
    padding: '4px 8px',
    margin: '4px 8px',
    outline: 'none'
  },
  deleteButton: {
    width: '32px',
    height: '32px',
    backgroundColor: '#ff4444',
    color: '#fff',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px 8px',
    padding: '0',
    '&:hover': {
      backgroundColor: '#ff5555'
    }
  },
  newCriterionButton: {
    color: '#3b82f6',
    backgroundColor: '#eff6ff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    height: '36px'
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginBottom: '16px',
    marginRight: '16px'
  },
  abortButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    height: '40px'
  },
  saveButton: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    height: '40px'
  },
  customTextarea: {
    border: '2px solid #e5e7eb',
    padding: '8px',
    width: '100%',
    height: '96px',
    '&:focus': {
      borderColor: '#3b82f6',
      outline: 'none'
    }
  },
  validationMessage: {
    color: 'red',
    marginBottom: '10px',
    fontSize: '12px'
  },
  labaleStyle: {
    color: 'red'
  }
}))

const AddEdit: React.FC<AlertProps> = ({
  open,
  togglePopup,
  callBackFetch,
  initialAlertData,
  alertDataProp,
  isEditMode,
  status,
  appId
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [alertData, setAlertData] = useState<AlertMessage>({
    AlertMessageUUID: alertDataProp?.AlertMessageUUID || '',
    Name: alertDataProp?.Name || '',
    Text: alertDataProp?.Text || '',
    AppId: alertDataProp?.AppId || appId,
    ValidFrom: alertDataProp?.ValidFrom || moment().format('YYYY-MM-DDTHH:mm'),
    ValidTo: alertDataProp?.ValidTo || moment().format('YYYY-MM-DDTHH:mm'),
    CreatedBy: alertDataProp?.CreatedBy || '',
    CreatedAt: alertDataProp?.CreatedAt || '',
    CriteriaUse: alertDataProp?.CriteriaUse || false,
    Link: alertDataProp?.Link || '',
    Criteria:
      alertDataProp?.Criteria?.map(criteria => ({
        CriteriaId: criteria.CriteriaId || 0,
        Name: criteria.Name || '',
        Value: criteria.Value || '',
        Comparison: criteria.Comparison || ''
      })) || []
  })

  const [formValid, setFormValid] = useState(true)
  const [validationMessage, setValidationMessage] = useState('')

  useEffect(() => {
    setAlertData({
      AlertMessageUUID: alertDataProp?.AlertMessageUUID || '',
      Name: alertDataProp?.Name || '',
      Text: alertDataProp?.Text || '',
      AppId: alertDataProp?.AppId || appId,
      ValidFrom: alertDataProp?.ValidFrom || moment().format('YYYY-MM-DDTHH:mm'),
      ValidTo: alertDataProp?.ValidTo || moment().format('YYYY-MM-DDTHH:mm'),
      CreatedBy: alertDataProp?.CreatedBy || '',
      CreatedAt: alertDataProp?.CreatedAt || '',
      CriteriaUse: alertDataProp?.CriteriaUse || false,
      Link: alertDataProp?.Link || '',
      Criteria:
        alertDataProp?.Criteria?.map(criteria => ({
          CriteriaId: criteria.CriteriaId || 0,
          Name: criteria.Name || '',
          Value: criteria.Value || '',
          Comparison: criteria.Comparison || ''
        })) || []
    })
  }, [alertDataProp])

  useEffect(() => {
    if (initialAlertData) {
      setAlertData(initialAlertData)
    }
  }, [initialAlertData])

  const handleAbort = () => {
    setAlertData(prevState => ({
      ...prevState,
      Name: alertDataProp?.Name ?? '',
      Text: alertDataProp?.Text ?? '',
      Link: alertDataProp?.Link ?? '',
      CriteriaUse: alertDataProp?.CriteriaUse ?? false,
      ValidFrom: alertDataProp?.ValidFrom ?? moment().format('YYYY-MM-DDTHH:mm:ss'),
      ValidTo: alertDataProp?.ValidTo ?? moment().format('YYYY-MM-DDTHH:mm:ss'),
      Criteria:
        alertDataProp?.Criteria?.map(criteria => ({
          CriteriaId: criteria.CriteriaId || 0,
          Name: criteria.Name || '',
          Value: criteria.Value || '',
          Comparison: criteria.Comparison || ''
        })) ?? []
    }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    if (type === 'radio' && name === 'CriteriaUse') {
      setAlertData((prevState: any) => ({
        ...prevState,
        [name]: value === 'true'
      }))
    } else if (name === 'ValidFrom' || name === 'ValidTo') {
      setAlertData((prevState: any) => ({
        ...prevState,
        [name]: value
      }))
    } else {
      setAlertData((prevState: any) => ({
        ...prevState,
        [name]: value
      }))
    }
  }

  const handleValueChange = (index: number, newValue: string) => {
    setAlertData(prevState => {
      const newCriteria = [...prevState.Criteria]
      newCriteria[index].Value = newValue
      return {
        ...prevState,
        Criteria: newCriteria
      }
    })
  }

  const handleComparisonChange = (index: number, newComparison: string) => {
    setAlertData(prevState => {
      const newCriteria = [...prevState.Criteria]
      newCriteria[index].Comparison = newComparison
      return {
        ...prevState,
        Criteria: newCriteria
      }
    })
  }

  const handleFieldChange = (index: number, selectedField: string) => {
    setAlertData(prevState => {
      const newCriteria = [...prevState.Criteria]
      newCriteria[index].Name = selectedField
      return {
        ...prevState,
        Criteria: newCriteria
      }
    })
  }

  const handleAddNewCriterion = () => {
    setAlertData(prevState => ({
      ...prevState,
      Criteria: [
        ...prevState.Criteria,
        {
          CriteriaId: 0,
          Name: '',
          Value: '',
          Comparison: ''
        }
      ]
    }))
  }

  const isValidCriteria = () => {
    return alertData.Criteria.every(criterion => criterion.Comparison && criterion.Value && criterion.Name)
  }

  const validateForm = () => {
    let isValid = true
    setValidationMessage('')

    if (!alertData.Name) {
      isValid = false
    }

    if (!alertData.Text) {
      isValid = false
    }

    const validFrom = isEditMode ? moment(alertData.ValidFrom, 'DD.MM.YYYY HH:mm') : moment(alertData.ValidFrom, 'YYYY-MM-DDTHH:mm:ss')
    const validTo = isEditMode ? moment(alertData.ValidTo, 'DD.MM.YYYY HH:mm') : moment(alertData.ValidTo, 'YYYY-MM-DDTHH:mm:ss')

    if (!validFrom.isValid() || !validTo.isValid()) {
      isValid = false
      setValidationMessage(t('Alert.DateValidateMsg'))
    } else if (validFrom.isAfter(validTo)) {
      isValid = false
      setValidationMessage(t('Alert.DateValidateMsg'))
    }

    setFormValid(isValid)
    return isValid
  }

  useEffect(() => {
    validateForm()
  }, [alertData])

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    const payload = {
      AlertMessageUUID: alertData.AlertMessageUUID || '',
      AppId: alertData.AppId,
      Name: alertData.Name,
      Text: alertData.Text,
      ValidFrom: isEditMode ? alertData.ValidFrom : moment(alertData.ValidFrom).format('DD.MM.YYYY HH:mm'),
      ValidTo: isEditMode ? alertData.ValidTo : moment(alertData.ValidTo).format('DD.MM.YYYY HH:mm'),
      CreatedBy: alertData.CreatedBy,
      CreatedAt: alertData.CreatedAt,
      CriteriaUse: alertData.CriteriaUse,
      Link: alertData.Link,
      Criteria: alertData.Criteria.map((criterion: any) => ({
        CriteriaId: criterion.CriteriaId,
        Name: criterion.Name,
        Value: criterion.Value,
        Comparison: criterion.Comparison
      }))
    }

    try {
      const response = await dispatch(AddEditAlertMessage(payload))

      if (response?.Data?.Status === AlertStatus.ALL) {
        notifier.success(t(response?.Data?.Message))
        callBackFetch(appId, status)
        togglePopup()
        handleAbort()
      } else {
        notifier.error(response?.Data?.Message)
      }
    } catch (error) {
      console.error('Error saving alert:', error)
      notifier.error(t('Alert.AddEditerror'))
    }
  }
  const handleDelete = (index: number) => {
    setAlertData(prevState => {
      const newCriteria = [...prevState.Criteria]
      newCriteria.splice(index, 1)
      alertData.Criteria = newCriteria

      return {
        ...prevState,
        Criteria: newCriteria
      }
    })
  }
  const handleOnCancel = () => {
    togglePopup()
    handleAbort()
  }
  return (
    <Dialog open={open} maxWidth='md'>
      <>
        <IconButton
          data-testid='close-button'
          className={`${classes.closeButton} ${classes.closeButtonStyle}`}
          onClick={handleOnCancel}
          size='small'
        >
          <CloseIcon />
        </IconButton>
        <>
          <DialogTitle className={classes.header}>{isEditMode ? t('Alert.EditAlert') : t('Alert.AddAlert')}</DialogTitle>

          <DialogContent>
            <div className={classes.container}>
              <div className={classes.formWrapper}>
                <div className={classes.formContent}>
                  <div className={classes.formGroup}>
                    <TextField
                      data-testid='alert-name-field'
                      name='Name'
                      value={alertData.Name}
                      label={t('Alert.Name')}
                      onChange={handleChange}
                      required
                      margin='normal'
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <div className={classes.datePickerContainer}>
                      {isEditMode ? (
                        <>
                          <div data-testid='alert-valid-from-field'>
                            <DateTimePicker
                              sx={{ width: '100%' }}
                              label={
                                <span>
                                  {t('Alert.ValidFrom')} <span className={classes.labaleStyle}>*</span>
                                </span>
                              }
                              value={
                                moment(alertData.ValidFrom, 'DD/MM/YYYY HH:mm').isValid()
                                  ? moment(alertData.ValidFrom, 'DD/MM/YYYY HH:mm')
                                  : null
                              }
                              minDate={moment()}
                              onChange={date => {
                                if (date) {
                                  setAlertData(prevState => ({
                                    ...prevState,
                                    ValidFrom: date.format('DD.MM.YYYY HH:mm')
                                  }))
                                }
                              }}
                            />
                          </div>
                          <div data-testid='alert-valid-to-field'>
                            <DateTimePicker
                              sx={{ width: '100%' }}
                              label={
                                <span>
                                  {t('Alert.ValidTo')}

                                  <span className={classes.labaleStyle}>*</span>
                                </span>
                              }
                              value={
                                moment(alertData.ValidTo, 'DD/MM/YYYY HH:mm').isValid()
                                  ? moment(alertData.ValidTo, 'DD/MM/YYYY HH:mm')
                                  : null
                              }
                              minDate={moment()}
                              onChange={date => {
                                if (date) {
                                  setAlertData(prevState => ({
                                    ...prevState,
                                    ValidTo: date.format('DD.MM.YYYY HH:mm')
                                  }))
                                }
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div data-testid='alert-valid-from-field'>
                            <DateTimePicker
                              sx={{ width: '100%' }}
                              label={
                                <span>
                                  {t('Alert.ValidFrom')}
                                  <span className={classes.labaleStyle}>*</span>
                                </span>
                              }
                              value={moment(alertData.ValidFrom).isValid() ? moment(alertData.ValidFrom) : null}
                              minDate={moment()}
                              onChange={date => {
                                if (date) {
                                  setAlertData(prevState => ({
                                    ...prevState,
                                    ValidFrom: date.format('YYYY-MM-DDTHH:mm:ss')
                                  }))
                                }
                              }}
                            />
                          </div>
                          <div data-testid='alert-valid-to-field'>
                            <DateTimePicker
                              sx={{ width: '100%' }}
                              label={
                                <span>
                                  {t('Alert.ValidTo')}

                                  <span className={classes.labaleStyle}>*</span>
                                </span>
                              }
                              value={moment(alertData.ValidTo).isValid() ? moment(alertData.ValidTo) : null}
                              minDate={moment()}
                              onChange={date => {
                                if (date) {
                                  setAlertData(prevState => ({
                                    ...prevState,
                                    ValidTo: date.format('YYYY-MM-DDTHH:mm:ss')
                                  }))
                                }
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {validationMessage && <div className={classes.validationMessage}>{validationMessage}</div>}
                  <div className={classes.formGroup}>
                    <TextareaAutosize
                      data-testid='alert-text-field'
                      name='Text'
                      className={classes.customTextarea}
                      value={alertData.Text}
                      placeholder={`${t('Alert.Text')}`}
                      onChange={handleChange}
                      required
                      minRows={4}
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>{t('Alert.UseCriteria')}</label>
                    <div className={classes.radioGroup}>
                      <label className={classes.radioLabel}>
                        <input
                          type='radio'
                          name='CriteriaUse'
                          value='true'
                          checked={alertData.CriteriaUse === true}
                          onChange={handleChange}
                          className={classes.radio}
                        />
                        {t('Alert.Yes')}
                      </label>
                      <label className={classes.radioLabel}>
                        <input
                          type='radio'
                          name='CriteriaUse'
                          value='false'
                          checked={alertData.CriteriaUse === false}
                          onChange={handleChange}
                          className={classes.radio}
                        />
                        {t('Alert.No')}
                      </label>
                    </div>
                  </div>
                  <div>
                    {alertData.CriteriaUse && (
                      <div className={classes.criteriaSection}>
                        <h3 className={classes.criteriaHeader}>{t('Alert.Criteria')}</h3>
                        <div className={classes.formGroup}>
                          <label className={classes.label}>Link</label>
                          <div className={classes.radioGroup}>
                            <label className={classes.radioLabel}>
                              <input
                                type='radio'
                                name='Link'
                                value='AND'
                                checked={alertData.Link === 'AND'}
                                onChange={handleChange}
                                className={classes.radio}
                              />
                              {t('Alert.And')}
                            </label>
                            <label className={classes.radioLabel}>
                              <input
                                type='radio'
                                name='Link'
                                value='OR'
                                checked={alertData.Link === 'OR'}
                                onChange={handleChange}
                                className={classes.radio}
                              />
                              {t('Alert.Or')}
                            </label>
                          </div>
                        </div>
                        <div className={classes.criteriaContainer}>
                          <div className={classes.criteriaTableHeader}>
                            <div className={classes.headerColumn}>Field</div>
                            <div className={classes.headerColumn}>Comparison</div>
                            <div className={classes.headerColumn}>Value(s)</div>
                          </div>
                          {alertData.Criteria.map((criteria, index) => (
                            <div key={index} className={classes.criteriaRow}>
                              <select
                                className={classes.select}
                                value={criteria.Name}
                                onChange={e => handleFieldChange(index, e.target.value)}
                              >
                                <option />
                                {editableFields.map((field, idx) => (
                                  <option key={idx} value={field}>
                                    {field}
                                  </option>
                                ))}
                              </select>
                              <select
                                className={classes.comparisonSelect}
                                value={criteria.Comparison}
                                onChange={e => handleComparisonChange(index, e.target.value)}
                              >
                                <option />
                                {comparisonOptions.map((field, idx) => (
                                  <option key={idx} value={field}>
                                    {field}
                                  </option>
                                ))}
                              </select>

                              <input
                                aria-label='CriteriaValue'
                                type='text'
                                value={criteria.Value}
                                className={classes.input}
                                onChange={e => handleValueChange(index, e.target.value)}
                              />
                              <TooltipButton
                                title={t('global.delete')}
                                icon='trash'
                                data-testid='deleteButton'
                                onClick={() => handleDelete(index)}
                              />
                            </div>
                          ))}
                        </div>
                        <div className={classes.buttonContainer}>
                          <Button
                            variant='contained'
                            className={`ui tiny primary ml-half ${classes.backButton}`}
                            onClick={handleAddNewCriterion}
                            disabled={alertData.Link === ''}
                          >
                            <i className='add icon white-icon' />
                            {t('Alert.Newcriteria')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={classes.actionButtons}>
                <Button variant='contained' className={`ui tiny primary ml-half ${classes.backButton}`} onClick={handleAbort}>
                  {t('Alert.Reset')}
                </Button>
                <Button
                  variant='contained'
                  className={`ui tiny primary ml-half ${classes.backButton}`}
                  onClick={handleSave}
                  disabled={
                    !formValid ||
                    !alertData.ValidFrom ||
                    !alertData.ValidTo ||
                    (alertData.CriteriaUse && (alertData.Criteria.length === 0 || !isValidCriteria()))
                  }
                >
                  {t('Alert.Save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </>
      </>
    </Dialog>
  )
}

export default AddEdit
